<template>
    <div class="table-responsive">
        <div v-if="isUserOwnerOfActiveOrganization" class="order-toolbar">
            <b-btn size="sm" type="default" @click="orderEditing = !orderEditing">
                <i class="fa" :class="orderEditing ? 'fa-check' : 'fa-sort'"></i>
                {{ $t(orderEditing ? 'message.finish_ordering' : 'message.edit_order') }}
            </b-btn>
        </div>
        <div class="dashboard-table">
            <div v-for="(item, index) in items" :key="itemKey(item, index)" class="dashboard-row" :class="{hide: !item.open, 'space-row':item.type==='space'}">
                <div class="dashboard-col name" :class="'level' + item.level">
                    <i v-if="item.children && ['space', 'heading', 'organization'].indexOf(item.type) === -1"
                       @click="toggle(item)" class="control fa" :class="{'fa-minus' : item.openChildren, 'fa-plus': !item.openChildren}"></i>
                    <span @click="toggle(item)" :class="{'clickable-link': item.children, bold: item.type === 'building'}">{{ item.name }}</span>
                </div>
                <div class="dashboard-col actions">
                    <div class="actions-toolbar hidden items-center gap-2 lg:inline-flex" v-if="['space', 'heading'].indexOf(item.type) === -1">
                        <div class="btn-group" role="group" v-if="orderEditing && item.owner && ['building', 'unit', 'transmitter'].indexOf(item.type) !== -1">
                            <b-btn size="xs" type="default" class="title" :disabled="!item.canMoveUp || isMoving(item)" @click="moveItem(item, 'up')" v-b-tooltip="$t('message.move_up')">
                                <i class="fa fa-arrow-up"></i>
                            </b-btn>
                            <b-btn size="xs" type="default" class="title" :disabled="!item.canMoveDown || isMoving(item)" @click="moveItem(item, 'down')" v-b-tooltip="$t('message.move_down')">
                                <i class="fa fa-arrow-down"></i>
                            </b-btn>
                        </div>
                        <div class="btn-group" role="group">
                            <b-btn size="xs" type="default" class="title" @click="modal('edit', item)"  v-b-tooltip="$t('message.edit')" v-if="item.owner && ['space', 'heading'].indexOf(item.type) === -1">
                                <i class="fa fa-pencil"></i>
                            </b-btn>
                            <b-btn size="xs" type="default" class="title" @click="modal('validation_transmitter', item)" v-b-tooltip="$t('message.add_validation_transmitter')" v-if="item.owner && !item.validation_transmitter && ['space', 'heading', 'difference', 'transmitter'].indexOf(item.type) === -1">
                                <i class="fa fa-check-square-o"></i>
                            </b-btn>
                        </div>
                        <div class="btn-group" role="group">
                            <b-btn size="xs" type="default" class="title" @click="modal('alarm', item)" :class="{'btn-success': item.alarms}" v-b-tooltip="$t('message.alarms')" v-if="['space', 'heading', 'difference'].indexOf(item.type) === -1">
                                <i class="fa fa-bell-o"></i>
                            </b-btn>
                            <b-btn size="xs" type="default" class="title" @click="modal('share', item)" :class="{'btn-success': item.sharing}" v-b-tooltip="$t('message.sharing')" v-if="['space', 'heading', 'difference'].indexOf(item.type) === -1">
                                <i class="fa fa-user-o"></i>
                            </b-btn>
                            <b-btn size="xs" type="default" class="title" @click="modal('remove', item)" v-b-tooltip="$t('message.remove')" v-if="item.owner && ['space', 'heading', 'organization'].indexOf(item.type) === -1">
                                <i class="fa fa-trash-o"></i>
                            </b-btn>
                        </div>
                    </div>
                    <div class="relative lg:hidden" v-if="['space', 'heading'].indexOf(item.type) === -1">
                        <button class="btn btn-default btn-xs" type="button" @click="toggleMenu(item)">
                            <span class="inline-block border-x-4 border-b-4 border-x-transparent border-b-gray-600"></span>
                        </button>
                        <ul v-if="openMenuId === itemKey(item)" class="absolute right-0 z-20 mt-1 min-w-[180px] rounded border border-gray-200 bg-white py-1 shadow-lg">
                            <li v-if="orderEditing && item.owner && ['building', 'unit', 'transmitter'].indexOf(item.type) !== -1">
                                <button type="button" class="block w-full px-3 py-1.5 text-left text-sm text-gray-700 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50" :disabled="!item.canMoveUp || isMoving(item)" @click="moveItem(item, 'up')"><i class="fa fa-arrow-up"></i> {{$t('message.move_up')}}</button>
                            </li>
                            <li v-if="orderEditing && item.owner && ['building', 'unit', 'transmitter'].indexOf(item.type) !== -1">
                                <button type="button" class="block w-full px-3 py-1.5 text-left text-sm text-gray-700 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50" :disabled="!item.canMoveDown || isMoving(item)" @click="moveItem(item, 'down')"><i class="fa fa-arrow-down"></i> {{$t('message.move_down')}}</button>
                            </li>
                            <li v-if="item.owner && ['space', 'heading'].indexOf(item.type) === -1" class="title">
                                <a href="#" class="block px-3 py-1.5 text-sm text-gray-700 no-underline hover:bg-gray-100" @click.prevent="modal('edit', item); openMenuId = null"><i class="fa fa-pencil"></i> {{$t('message.edit')}}</a>
                            </li>
                            <li v-if="item.owner && !item.validation_transmitter && ['space', 'heading', 'difference', 'transmitter'].indexOf(item.type) === -1" class="title">
                                <a href="#" class="block px-3 py-1.5 text-sm text-gray-700 no-underline hover:bg-gray-100" @click.prevent="modal('validation_transmitter', item); openMenuId = null"><i class="fa fa-check-square-o"></i> {{$t('message.add_validation_transmitter')}}</a>
                            </li>
                            <li v-if="['space', 'heading', 'difference'].indexOf(item.type) === -1" class="title">
                                <a href="#" class="block px-3 py-1.5 text-sm text-gray-700 no-underline hover:bg-gray-100" :class="{'text-success': item.alarms}" @click.prevent="modal('alarm', item); openMenuId = null"><i class="fa fa-bell-o"></i> {{$t('message.alarms')}}</a>
                            </li>
                            <li v-if="['space', 'heading', 'difference'].indexOf(item.type) === -1" class="title">
                                <a href="#" class="block px-3 py-1.5 text-sm text-gray-700 no-underline hover:bg-gray-100" :class="{'text-success': item.sharing}" @click.prevent="modal('share', item); openMenuId = null"><i class="fa fa-user-o"></i> {{$t('message.sharing')}}</a>
                            </li>
                            <li v-if="item.owner && ['space', 'heading', 'organization'].indexOf(item.type) === -1">
                                <a href="#" class="block px-3 py-1.5 text-sm text-gray-700 no-underline hover:bg-gray-100" @click.prevent="modal('remove', item); openMenuId = null"><i class="fa fa-trash-o"></i> {{$t('message.remove')}}</a>
                            </li>
                        </ul>
                    </div>
                </div>
                <div class="dashboard-col sparkline">
                    <trend v-if="item.trend"
                            :data="item.trend"
                            :gradient="['#50d373', '#c2d350', '#d35050']"
                            :width="100"
                            :height="30"
                            :stroke-width="2"
                            :auto-draw-duration="500"
                            auto-draw
                            smooth>
                    </trend>
                </div>
                <div class="dashboard-col data" v-for="data in item.data" :style="{'background-color': data.color}">
                    <template v-if="item.type === 'heading' || item.type === 'space'">{{ data.value }}</template>
                    <template v-else>{{ ((period == 'year' || unit == 'watts') && showUnit !== 'price' ? (data.value / 1000) : data.value) | round(2) }}</template>
                </div>
            </div>
            <b-modal v-model="removeTransmitter" :title="$t('message.remove')" ref="modal" id="modal-remove">
                <div class="space-y-3 pb-1 text-sm leading-relaxed text-gray-700">
                    <div v-if="removeToItem" class="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-800">
                        <div class="font-medium">{{ removeToItem.name }}</div>
                        <div v-if="removeTransmitterSerial" class="mt-1 text-gray-600">
                            {{ $t('message.serial_number') }}: {{ removeTransmitterSerial }}
                        </div>
                        <div v-if="removeToItem.path" class="mt-1 text-gray-500">
                            {{ removeToItem.path }}
                        </div>
                        <div class="mt-1 text-gray-500">
                            {{ meterLabel }}
                        </div>
                    </div>
                    <p class="m-0">{{ $t('message.confirm_remove_transmitter') }}</p>
                    <p class="m-0">{{ $t('message.confirm_remove_transmitter_billing') }}</p>
                    <p class="m-0">{{ $t('message.confirm_remove_transmitter_unused') }}</p>
                </div>
                <template #footer>
                    <div class="flex w-full flex-wrap items-center justify-end gap-2">
                        <b-btn @click="remove" type="danger">{{ $t('message.confirm_remove_transmitter_mistake') }}</b-btn>
                        <b-btn @click="removeTransmitter=false">{{ $t('message.cancel') }}</b-btn>
                    </div>
                </template>
            </b-modal>


            <b-modal v-model="openRemoveModal" :title="$t('message.remove')" ref="modal" id="modal-remove-transmitter">
                <p class="m-0 pb-1 text-sm leading-relaxed text-gray-700">{{removeModalText}}</p>
                <template #footer>
                    <div class="flex w-full flex-wrap items-center justify-end gap-2">
                        <b-btn @click="openRemoveModal=false">{{ $t('message.no') }}</b-btn>
                        <b-btn @click="remove" type="danger">{{ $t('message.yes') }}</b-btn>
                    </div>
                </template>
            </b-modal>

            <edit-building ref="editBuildingModal"/>
            <edit-unit ref="editUnitModal"/>
            <edit-transmitter ref="editTransmitterModal"/>
            <edit-organization ref="editOrganizationModal"/>
            <share ref="shareModal"/>
            <alarm :unit="unit" ref="alarmModal" />
            <create-transmitter-2 ref="addTransmitterModal" />
        </div>
        <div class="legend-box text-muted">
          <div class="mr-1 inline">{{ $t('message.lowest_consumption_in_a_given_row') }}</div>
            <div class="inline legend" v-for="c in ['hsl(136 ,60%, 57%)','hsl(129 ,60%, 57%)','hsl(102 ,60%, 57%)','hsl(75 ,60%, 57%)','hsl(48 ,60%, 57%)','hsl(21 ,60%, 57%)', 'hsl(0 ,60%, 57%)']" :style="{'background-color': c}">

            </div>
          <div class="ml-1 inline">{{ $t('message.highest_consumption_in_a_given_row') }}</div>
        </div>
    </div>
</template>

<script>
    import _ from 'lodash'
    import mixins from '../../modules/mixins'
    import axios from 'axios'
    import {EditBuilding, EditUnit, EditTransmitter, EditOrganization, Share, Alarm, CreateTransmitter2} from './modal'
    import {mapState, mapMutations} from 'vuex'
    import eventBus from '../../modules/eventBus'
    // Green 136,60,57 - Red 0,60,57

    const emptyLine = (size, extend = {}) => {
        return _.defaults(extend, {
            name: '',
            type: 'space',
            trend: false,
            level: 0,
            open: true,
            data: _.fill([], {value: ''}, 0, size)
        })
    }

    const color = (value, min, max) => {
        if (max.value - min.value === 0) {
            return 'hsl(136 ,60%, 57%)'
        }
        const h = 136 - Math.floor((((value.value - min.value) / (max.value - min.value))) * 136)
        return `hsl(${h} ,60%, 57%)`
    }

    const computeColors = (data, grey, absv) => {
        const max = _.maxBy(data, 'value')
        const min = _.minBy(data, 'value')
        if (data.length > 0 && absv === true) {
            const m = Math.max(Math.abs(max.value), Math.abs(min.value))
            return _.map(data, (i) => {
                i.color = grey ? 'grey' : color({value: Math.abs(i.value)}, {value: 0}, {value: m})
                return i
            })
        } else {
            return _.map(data, (i) => {
                i.color = grey ? 'grey' : color(i, min, max)
                return i
            })
        }
    }

    const updateUnit = (data, unit, pricePerLiter) => {
        if (unit !== 'price') {
            return data
        }
        return _.map(data, i => {
            i.value = i.value * pricePerLiter
            return i
        })
    }

    const bucketIdentity = (point) => {
        if (!point) {
            return null
        }
        if (point.timestamp !== undefined && point.timestamp !== null && point.timestamp !== '') {
            return {
                id: `timestamp:${point.timestamp}`,
                type: 'timestamp',
                order: Number(point.timestamp)
            }
        }
        if (point.date !== undefined && point.date !== null && point.date !== '') {
            return {
                id: `date:${point.date}`,
                type: 'date',
                order: String(point.date)
            }
        }
        return null
    }

    const numericValue = (point) => {
        const value = Number(point && point.value)
        return Number.isFinite(value) ? value : 0
    }

    const aggregateByBucket = (left, right, aggregate) => {
        const series = [left || [], right || []]
        const hasCanonicalBuckets = _.some(series, values => _.some(values, bucketIdentity))
        const buckets = new Map()
        let sequence = 0

        series.forEach((values, seriesIndex) => {
            values.forEach((point, pointIndex) => {
                const identity = bucketIdentity(point)
                if (hasCanonicalBuckets && !identity && numericValue(point) === 0) {
                    return
                }
                const bucket = identity || {
                    id: hasCanonicalBuckets ? `unkeyed:${seriesIndex}:${pointIndex}` : `position:${pointIndex}`,
                    type: 'position',
                    order: pointIndex
                }
                if (!buckets.has(bucket.id)) {
                    buckets.set(bucket.id, {
                        identity: bucket,
                        point: _.cloneDeep(point),
                        sequence: sequence++,
                        values: [0, 0]
                    })
                }
                buckets.get(bucket.id).values[seriesIndex] += numericValue(point)
            })
        })

        const typeOrder = {timestamp: 0, date: 1, position: 2}
        return Array.from(buckets.values())
            .sort((a, b) => {
                if (a.identity.type === b.identity.type && a.identity.order !== b.identity.order) {
                    return a.identity.order < b.identity.order ? -1 : 1
                }
                if (a.identity.type !== b.identity.type) {
                    return typeOrder[a.identity.type] - typeOrder[b.identity.type]
                }
                return a.sequence - b.sequence
            })
            .map(bucket => Object.assign({}, bucket.point, {
                value: aggregate(bucket.values[0], bucket.values[1])
            }))
    }

    const computeDiffs = (in1, in2) => {
        if (!in1 || !in2) {
            return []
        }
        return aggregateByBucket(in2, in1, (control, measured) => control - measured)
    }

    const getType = (type, data, unit, cold, hot, hide) => {
        if (!data) {
            return null
        }
        if (type === 'total') {
            if (hide == null) {
                hide = {
                    cold: false,
                    hot: false
                }
            }
            const coldDataInput = hide['cold'] ? data['cold'].map(x => {
                return {
                    ...x,
                    value: 0,
                }
            }) : data['cold']
            const hotDataInput = hide['hot'] ? data['hot'].map(x => {
                return {
                    ...x,
                    value: 0,
                }
            }) : data['hot']

            const coldData = updateUnit(_.cloneDeep(coldDataInput), unit, cold)
            const hotData = updateUnit(_.cloneDeep(hotDataInput), unit, hot)
            return aggregateByBucket(hotData, coldData, (hotValue, coldValue) => hotValue + coldValue)
        }
        return updateUnit(_.cloneDeep(data[type]), unit, (type === 'hot' ? hot : cold))
    }

    const revertValues = (data, revert) => {
        if (!revert) {
            return data
        }
        return _.map(data, (i) => {
            i.value = -i.value
            return i
        })
    }

    const flatten = (type, tree, level = 0, unit = 'liters', owner, translator, isOpen) => {
        // const openChild = level < 2 // !(level >= 2 || (level === 1 && tree.length > 1))
        let items = []
        let complete = 0
        tree.forEach((item) => {
            const openChild = isOpen(item.path, level < 2)
            if (type !== 'total' && item.hide && item.hide[type]) {
                return
            }
            if (item.heading) {
                items.push({
                    name: '',
                    type: 'heading',
                    trend: false,
                    open: true,
                    data: item.heading
                })
            }
            if (item.type === 'dummy') {
                if (item.children) {
                    const children = flatten(type, item.children, level + 1, unit, owner, translator, isOpen)
                    items = _.concat(items, children)
                }
                return items
            }
            const flowmeter = getType(type, item.flowmeter, unit, item.pricePerLiterCold, item.pricePerLiterHot)

            let useFlowmetter = !!flowmeter
            let hide = false
            let grey = false
            if (useFlowmetter && (type === 'cold' || type === 'hot')) {
                const hasMeasuredFlowmeterData = _.some(flowmeter, point => Number(point.value) !== 0)
                useFlowmetter = item.hideFlowmeter[type] === false || hasMeasuredFlowmeterData
                hide = !useFlowmetter
            }
            if (useFlowmetter && item.type === 'organization') {
                const sum = _.sumBy(flowmeter, 'value')
                if (sum === 0) {
                    // useFlowmetter = false
                    grey = true
                }
            }
            let diffs = computeColors(revertValues(computeDiffs(getType(type, item.data, unit, item.pricePerLiterCold, item.pricePerLiterHot), flowmeter), grey), false, true)
            let data = useFlowmetter ? flowmeter : getType(type, item.data, unit, item.pricePerLiterCold, item.pricePerLiterHot)
            data = computeColors(data, grey, false)
            if (grey) {
                const temp = diffs
                diffs = data
                data = temp
            }
            // if (unit === 'price') {
            //     data = updateUnit(data, pricePerLitter)
            // }
            const row = {
                name: item.name + (!grey && useFlowmetter && diffs.length > 0 ? ' (' + translator('message.postfix_validation_transmitter') + ')' : ''),
                level: level,
                type: item.type,
                validation_transmitter: diffs.length > 0,
                alarms: item.alarms[type],
                sharing: item.sharing,
                id: item.id,
                sortOrder: item.sortOrder,
                canMoveUp: item.canMoveUp === true,
                canMoveDown: item.canMoveDown === true,
                path: item.path,
                serial_number: item.serial_number || null,
                openChildren: true,
                trend: _.sumBy(data, 'value') == 0 ? false : data,
                open: true,
                owner: owner,
                data: _.concat(hide ? data.map(x => {
                    x.value = ''
                    x.color = '#EDF2F7'
                    return x
                }) : data, {
                    value: hide ? 0 : _.sumBy(data, 'value')
                })
            }
            items.push(row)

            let row2 = []
            if (useFlowmetter && diffs.length > 0) {
                row2.push({
                    name: grey ? translator('message.validation_transmitter_not_available') : translator('message.difference_from_validation_transmitter'),
                    level: level + 1,
                    type: 'difference',
                    id: item.type + '-' + item.id,
                    owner: owner,
                    openChildren: true,
                    trend: _.sumBy(diffs, 'value') == 0 ? false : diffs,
                    open: true,
                    data: _.concat(diffs, {
                        value: _.sumBy(diffs, 'value')
                    })
                })
            }
            if (item.children) {
                const children = _.concat(emptyLine(data, {level: level + 1}), row2, flatten(type, item.children, level + 1, unit, owner, translator, isOpen))
                items = _.concat(items, children)
                row.children = children
            } else if (row2.length > 0) {
                const children = _.concat(emptyLine(data, {level: level + 1}), row2)
                items = _.concat(items, children)
                row.children = children
            }
            complete++
            if (complete < tree.length && row.children) {
                const empty = emptyLine(data, {level: level + 1})
                row.children.push(empty)
                items.push(empty)
            }
            if (!openChild && row.children) {
                row.openChildren = false
                row.children.forEach((child) => {
                    child.openChildren = false
                    child.open = false
                })
            }
        })
        return items
    }

    export default {
        name: 'dashboard-table',
        compatConfig: {
            WATCH_ARRAY: false
        },
        mixins: [mixins],
        components: {
            CreateTransmitter2,
            EditTransmitter,
            EditUnit,
            EditBuilding,
            EditOrganization,
            Share,
            Alarm
        },
        props: {
            type: {
                type: String
            },
            data: {
                type: Array
            },
            unit: {
                type: String
            },
            period: {
                type: String
            },
            medium: {
                type: String
            }
        },
        data () {
            return {
                loaded: false,
                items: [],
                openRemoveModal: false,
                removeModalText: this.$t('message.confirm_remove_object'),
                removeToItem: null,
                lastData: null,
                currentUnit: null,
                removeTransmitter: false,
                openMenuId: null,
                movingItemKey: null,
                orderEditing: false
            }
        },
        computed: {
            ...mapState('tree', ['opened']),
            meterLabel () {
                switch (this.medium) {
                case 'power':
                    return this.$t('message.electricity_meter')
                case 'gas':
                    return this.$t('message.gas_meter')
                case 'heat':
                    return this.$t('message.heat_meter')
                default:
                    return this.$t('message.water_meter')
                }
            },
            meterLabelLower () {
                const label = this.meterLabel
                if (!label) {
                    return ''
                }
                return label.charAt(0).toLocaleLowerCase() + label.slice(1)
            },
            removeTransmitterSerial () {
                if (!this.removeToItem || !this.removeToItem.serial_number) {
                    return ''
                }
                const serial = String(this.removeToItem.serial_number)
                if (this.medium === 'heat') {
                    try {
                        const decoded = this.decodeITN(serial.replace(/-/g, ''))
                        return decoded != null ? decoded : serial
                    } catch (e) {
                        return serial
                    }
                }
                return serial
            }
        },
        watch: {
            data (data) {
                this.lastData = data
                if (!this.currentUnit) {
                    this.currentUnit = this.showUnit
                }
                this.items = this.processItems(data, this.currentUnit)
            },
            showUnit (unit) {
                if (this.currentUnit === unit) {
                    return
                }
                this.currentUnit = unit
                this.items = this.processItems(this.lastData, unit)
            }
        },
        methods: {
            ...mapMutations('tree', ['setOpen']),
            itemKey (item, index = null) {
                if (item.id !== undefined && item.id !== null) {
                    return `${item.type}-${item.id}`
                }
                return `${item.type}-${item.name}-${index !== null ? index : ''}`
            },
            toggleMenu (item) {
                const key = this.itemKey(item)
                this.openMenuId = this.openMenuId === key ? null : key
            },
            isMoving (item) {
                return this.movingItemKey === `${item.type}-${item.id}`
            },
            moveItem (item, direction) {
                if (this.isMoving(item) || (direction === 'up' ? !item.canMoveUp : !item.canMoveDown)) {
                    return
                }
                this.movingItemKey = `${item.type}-${item.id}`
                axios.put('/api/v2/tree-order', {
                    type: item.type,
                    id: item.id,
                    direction
                }).then(() => {
                    this.openMenuId = null
                    eventBus.emit('change', {force: true, reload: true, source: 'tree-order'})
                }).catch(err => {
                    this.notify('danger', this.getErrorMessage(err))
                }).finally(() => {
                    this.movingItemKey = null
                })
            },
            isOpen (id, defaultStatus) {
                if (this.opened[id]) {
                    return this.opened[id]
                } else {
                    this.setOpen({
                        id: id,
                        status: defaultStatus
                    })
                    return defaultStatus
                }
            },
            processItems (tree, unit = 'liters') {
                if (!tree) {
                    return []
                }
                return flatten(this.type, tree, 0, unit, this.isUserOwnerOfActiveOrganization, this.$t, this.isOpen)
            },
            objectText (type) {
                switch (type) {
                case 'building':
                    return this.$t('message.postfix_building')
                case 'unit':
                    return this.$t('message.postfix_unit')
                case 'transmitter':
                    return this.meterLabelLower
                case 'difference':
                    return this.$t('message.postfix_validation_transmitter')
                }
            },
            modal (type, item) {
                switch (type) {
                case 'alarm':
                    this.$refs.alarmModal.show(item, this.type)
                    break
                    case 'validation_transmitter':
                    this.$refs.addTransmitterModal.show(item, true, {'water': 1, 'total': 1, 'hot': 1, 'cold': 1, 'power': 2, 'gas': 3, 'heat': 4}[this.medium])
                    break
                case 'share':
                    this.$refs.shareModal.show(item)
                    break
                case 'remove':
                    if (item.type === 'transmitter') {
                        this.removeTransmitter = true
                        this.removeToItem = item
                        return
                    }
                    this.removeToItem = item
                    this.removeModalText = this.$t('message.confirm_remove_prefix') + ' ' + this.objectText(item.type) + (item.type !== 'difference' ? ' "' + item.name + '"?' : '')
                    this.openRemoveModal = true
                    break
                case 'edit':
                    if (item.type === 'building') {
                        this.$refs.editBuildingModal.show(item)
                    } else if (item.type === 'unit') {
                        this.$refs.editUnitModal.show(item)
                    } else if (item.type === 'transmitter') {
                        this.$refs.editTransmitterModal.show(item)
                    } else if (item.type === 'difference') {
                        this.$refs.editTransmitterModal.show(item)
                    } else if (item.type === 'organization') {
                        this.$refs.editOrganizationModal.show(item)
                    }
                    break
                }
            },
            remove () {
                axios.post('/api/v1/remove-entity', {
                    id: this.removeToItem.id,
                    type: this.removeToItem.type
                }).then(response => {
                    this.openRemoveModal = false
                    this.removeTransmitter = false
                    if (response.data && response.data.message) {
                        this.notify('info', response.data.message)
                    }
                    eventBus.emit('change', {force: true, source: 'table-rem'})
                }).catch(err => {
                    this.notify('danger', this.getErrorMessage(err))
                })
            },
            toggle (item) {
                item.openChildren = !item.openChildren
                this.setOpen({
                    id: item.path,
                    status: item.openChildren
                })
                item.children.forEach((child) => {
                    const open = item.openChildren && [item.level, item.level + 1].indexOf(child.level) > -1
                    child.open = open
                    child.openChildren = false
                })
            }
        },
        mounted () {
            if (this.data) {
                this.lastData = this.data
                if (!this.currentUnit) {
                    this.currentUnit = this.showUnit
                }
                this.items = this.processItems(this.data, this.currentUnit)
            }
        }
    }
</script>

<style type="scss" scoped>
    .dashboard-table {
        display: table;
        width: 100%;
        margin-top: 20px;
        border-collapse:collapse;
    }

    .order-toolbar {
        display: flex;
        justify-content: flex-end;
        margin-top: 10px;
        margin-bottom: -10px;
    }

    @media screen and (max-width: 1200px) {
        .table-responsive {
            padding-bottom: 140px; /*Fix for the dropdown in the last row*/
        }
    }
    .dashboard-row {
        display: table-row;
        font-size: 11px;
    }
    .dashboard-row.space-row>.dashboard-col {
        height: 3px;
    }
    .dashboard-col {
        display: table-cell;
        text-align: center;
        vertical-align: middle;
        min-height: 5px;
    }

    .legend-box {
      padding: 4px;
      float: right;
      font-size: 85%;
    }

    .legend-box > .inline {
      display: inline-block;
    }

    .legend-box > .legend {
      width: 25px;
      height: 10px;
    }

    .clickable-link {
        color: #337ab7;
        cursor:pointer;
    }

    .dashboard-col.actions {
        text-align: left;
        position: relative;
        z-index: 2;
        width: 1%;
        white-space: nowrap;
        padding-right: 8px;
        vertical-align: middle;
    }

    .actions .btn-group {
        display: inline-flex;
        vertical-align: middle;
        gap: 0;
    }

    .actions :deep(.btn-group > .btn) {
        margin-left: -1px;
        border-radius: 0;
        position: relative;
        z-index: 1;
        pointer-events: auto;
        cursor: pointer;
    }

    .actions :deep(.btn-group > .btn:hover) {
        z-index: 2;
    }

    .actions :deep(.btn-group > .btn:first-child) {
        margin-left: 0;
        border-top-left-radius: 3px;
        border-bottom-left-radius: 3px;
    }

    .actions :deep(.btn-group > .btn:last-child) {
        border-top-right-radius: 3px;
        border-bottom-right-radius: 3px;
    }

    .actions .title {
        font-size: 13px;
    }

    .actions .title i {
        line-height: 1;
        pointer-events: none;
    }

    .actions :deep(.btn-success:hover i) {
        color: #333;
    }

    .dashboard-col.sparkline {
        width: 100px;
        position: relative;
        z-index: 0;
    }
    .dashboard-col:last-of-type {
        font-weight: bold;
    }
    .name {
        text-align: left;
        width: 30%;
        max-width: 30vw;
        overflow: hidden;
        text-overflow: ellipsis;
        font-size: 14px;
        white-space: nowrap;
    }
    .dropdown {
        margin-left:5px;
    }
    .data {
        min-width: 10px;
        padding: 0px;
    }
    .hide {
        display: none;
    }
    .level0 {
        padding-left: 0px;
    }
    .level1 {
        padding-left: 5px;
    }
    .level2 {
        padding-left: 10px;
    }
    .level3 {
        padding-left: 25px;
    }

    .control {
        cursor: pointer;
        display: inline-block;
        width: 1.1em;
        margin-right: 4px;
        text-align: center;
    }

    .bold {
        font-weight: bold;
    }
</style>
